import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from '../support/AxiosSupport';
import MonthlyBarChart from './MonthlyBarChart';
import Spinner from './Spinner';
import {pluralForm, pluralItems, pluralItemsOf} from '../utils/plural';

interface MarketplaceStat {
    code: string | null;
    nameRu: string;
    placements: number;
    sold: number;
}

interface HoldingStat {
    scenario: string;
    count: number;
    averageDays: number;
    medianDays: number;
}

interface DisposalAnalytics {
    money: {
        totalRevenueRub: number;
        recoveryShare: number | null;
        averageCheckRub: number;
        medianDaysToSell: number | null;
        listedCount: number;
        soldCount: number;
        marketplaces: MarketplaceStat[];
    };
    ecology: {
        secondLifeCount: number;
        landfillCount: number;
        secondLifeShare: number | null;
        savedKilograms: number | null;
        estimated: boolean;
    };
    discipline: {
        decidedCount: number;
        completedCount: number;
        completionShare: number | null;
        pendingCount: number;
        returnedCount: number;
        oldestPendingDays: number | null;
        holdingByChannel: HoldingStat[];
        holdingBySource: HoldingStat[];
    };
}

interface WardrobeTimeline {
    months: {month: string; bought: number; disposed: number; itemCount: number; valueRub: number}[];
    itemsWithoutPurchaseDate: number;
    itemsWithoutPrice: number;
    /** Куплены до первой записи аккаунта — входят в стартовый остаток. */
    itemsBeforeFirstRecord?: number;
    startMonth?: string | null;
}

const SCENARIO_LABELS: Record<string, string> = {
    SELL: 'Продажа',
    DONATE: 'Пожертвование',
    GIVE_AWAY: 'Отдать другу',
    DISPOSE: 'Утилизация',
    DECLUTTER_CONFIRM: 'Разбор гардероба',
    TRASH_CLEAR: 'Завершение выбытия',
    THROW_AWAY: 'Вручную',
    SALE: 'По объявлению',
    RESTORE: 'Возврат',
    AUTOPILOT: 'Автопилот',
    STYLIST: 'Стилист',
};

const scenarioLabel = (code: string) => SCENARIO_LABELS[code] ?? code;
const money = (n: number) => Math.round(n).toLocaleString('ru-RU') + ' ₽';
const percent = (share: number | null) => (share == null ? '—' : Math.round(share * 100) + '%');

function Metric({label, value, hint, testId}: {
    label: string;
    value: string;
    hint?: string;
    testId: string;
}) {
    return (
        <div className="card p-4" data-testid={testId}>
            <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">{value}</p>
            {hint && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
        </div>
    );
}

function HoldingTable({title, stats, testId}: {
    title: string;
    stats: HoldingStat[];
    testId: string;
}) {
    if (stats.length === 0) return null;
    return (
        <div data-testid={testId}>
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</h4>
            <div className="mt-2 overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="text-left text-gray-500 dark:text-gray-400">
                            <th className="py-1 pr-4 font-normal">Сценарий</th>
                            <th className="py-1 pr-4 font-normal">Вещей</th>
                            <th className="py-1 pr-4 font-normal">Медиана</th>
                            <th className="py-1 font-normal">Среднее</th>
                        </tr>
                    </thead>
                    <tbody>
                        {stats.map(s => (
                            <tr key={s.scenario} className="border-t border-gray-100 dark:border-ink-700">
                                <td className="py-1 pr-4 text-gray-900 dark:text-gray-100">
                                    {scenarioLabel(s.scenario)}
                                </td>
                                <td className="py-1 pr-4 text-gray-700 dark:text-gray-300">{s.count}</td>
                                <td className="py-1 pr-4 text-gray-700 dark:text-gray-300">
                                    {s.medianDays} дн.
                                </td>
                                <td className="py-1 text-gray-500 dark:text-gray-400">
                                    {s.averageDays} дн.
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/**
 * Вкладка «Выбытие»: что происходит с вещами после решения.
 *
 * Три группы намеренно не сводятся в один индекс — деньги, экология и дисциплина отвечают на
 * разные вопросы и улучшаются разными действиями. Медиана времени в холдинге стоит рядом со
 * средним: одна забытая на полгода вещь утаскивает среднее так, что оно перестаёт что-либо
 * описывать.
 */
export default function DisposalTab({ownerId}: {ownerId: string | null}) {
    const [disposal, setDisposal] = useState<DisposalAnalytics | null>(null);
    const [timeline, setTimeline] = useState<WardrobeTimeline | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const api = await createAxiosInstance();
            const params = ownerId ? {ownerId} : {};
            const [d, t] = await Promise.all([
                api.get<DisposalAnalytics>('/analytics/disposal', {params}),
                api.get<WardrobeTimeline>('/analytics/wardrobe-timeline',
                    {params: {...params, months: 12}}),
            ]);
            setDisposal(d.data);
            setTimeline(t.data);
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [ownerId]);

    useEffect(() => {
        void load();
    }, [load]);

    if (loading) {
        return <div className="flex justify-center py-10" data-testid="disposal-loading"><Spinner/></div>;
    }
    if (error || !disposal || !timeline) {
        return (
            <div className="card p-6 text-center" data-testid="disposal-error">
                <p className="text-red-600 dark:text-red-400">Не удалось загрузить аналитику выбытия.</p>
                <button type="button" onClick={() => void load()}
                        className="btn-secondary mt-3 min-h-[44px] px-4 rounded-xl text-sm">
                    Повторить
                </button>
            </div>
        );
    }

    const {money: m, ecology: e, discipline: d} = disposal;
    // Вещи, купленные до первой записи: поле additive, поэтому нормализуем один раз.
    const boughtBeforeFirstRecord = timeline.itemsBeforeFirstRecord ?? 0;

    return (
        <div className="space-y-6" data-testid="disposal-tab">
            <section className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Деньги</h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <Metric testId="disposal-revenue" label="Выручено" value={money(m.totalRevenueRub)}/>
                    <Metric testId="disposal-recovery" label="Доля возврата"
                            value={percent(m.recoveryShare)}
                            hint={m.recoveryShare == null
                                ? 'Нужны цены покупки проданных вещей'
                                : 'От цены покупки проданных вещей'}/>
                    <Metric testId="disposal-check" label="Средний чек" value={money(m.averageCheckRub)}/>
                    <Metric testId="disposal-sold" label="Продалось"
                            value={`${m.soldCount} из ${m.listedCount}`}
                            hint={m.medianDaysToSell == null
                                ? undefined : `Медиана ${m.medianDaysToSell} дн. до продажи`}/>
                </div>
                {m.marketplaces.length > 0 && (
                    <div className="overflow-x-auto" data-testid="disposal-marketplaces">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="text-left text-gray-500 dark:text-gray-400">
                                    <th className="py-1 pr-4 font-normal">Площадка</th>
                                    <th className="py-1 pr-4 font-normal">Размещений</th>
                                    <th className="py-1 font-normal">Продано</th>
                                </tr>
                            </thead>
                            <tbody>
                                {m.marketplaces.map(mp => (
                                    <tr key={mp.code ?? mp.nameRu}
                                        className="border-t border-gray-100 dark:border-ink-700">
                                        <td className="py-1 pr-4 text-gray-900 dark:text-gray-100">{mp.nameRu}</td>
                                        <td className="py-1 pr-4 text-gray-700 dark:text-gray-300">{mp.placements}</td>
                                        <td className="py-1 text-gray-700 dark:text-gray-300">{mp.sold}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Экология</h3>
                <div className="grid gap-3 sm:grid-cols-3">
                    <Metric testId="disposal-second-life" label="Вторая жизнь"
                            value={`${e.secondLifeCount}`}
                            hint="Продано, пожертвовано или отдано"/>
                    <Metric testId="disposal-landfill" label="На утилизацию" value={`${e.landfillCount}`}/>
                    <Metric testId="disposal-saved-weight" label="Не поехало на свалку"
                            value={e.savedKilograms == null ? '—' : `≈ ${e.savedKilograms} кг`}
                            hint={e.estimated ? 'Оценка по категориям, не факт' : undefined}/>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Вторую жизнь получили {percent(e.secondLifeShare)} выбывших вещей.
                </p>
            </section>

            <section className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Дисциплина</h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <Metric testId="disposal-completion" label="Доведено до конца"
                            value={percent(d.completionShare)}
                            hint={`${d.completedCount} из ${d.decidedCount} решений`}/>
                    <Metric testId="disposal-pending" label="Ждёт физического шага"
                            value={`${d.pendingCount}`}
                            hint={d.oldestPendingDays == null
                                ? undefined : `Самая старая — ${d.oldestPendingDays} дн.`}/>
                    <Metric testId="disposal-returned" label="Вернулось в гардероб"
                            value={`${d.returnedCount}`}
                            hint={d.returnedCount > 0 ? 'Возможно, разбор был слишком жёстким' : undefined}/>
                </div>
                <HoldingTable testId="disposal-holding-channel"
                              title="Сколько вещи ждут — по каналу выбытия"
                              stats={d.holdingByChannel}/>
                <HoldingTable testId="disposal-holding-source"
                              title="Сколько вещи ждут — по тому, кто довёл до конца"
                              stats={d.holdingBySource}/>
            </section>

            <section className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Гардероб по месяцам
                </h3>
                <MonthlyBarChart testId="timeline-flow"
                                 points={timeline.months.map(p => ({
                                     month: p.month, primary: p.bought, secondary: p.disposed,
                                 }))}
                                 primaryLabel="Куплено" secondaryLabel="Выбыло"/>
                <MonthlyBarChart testId="timeline-count"
                                 points={timeline.months.map(p => ({month: p.month, primary: p.itemCount}))}
                                 primaryLabel="Вещей на конец месяца"/>
                <MonthlyBarChart testId="timeline-value"
                                 points={timeline.months.map(p => ({month: p.month, primary: p.valueRub}))}
                                 primaryLabel="Стоимость гардероба"
                                 format={v => (v === 0 ? '' : Math.round(v / 1000) + 'к')}/>
                {(timeline.itemsWithoutPurchaseDate > 0 || timeline.itemsWithoutPrice > 0
                    || boughtBeforeFirstRecord > 0) && (
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="timeline-caveat">
                        Ряды восстановлены по датам карточек с месяца первой записи; ожидание передачи владением не считается.
                        {timeline.itemsWithoutPurchaseDate > 0
                            && ` Для ${pluralItemsOf(timeline.itemsWithoutPurchaseDate)} без даты покупки взята дата добавления.`}
                        {timeline.itemsWithoutPrice > 0
                            && ` ${pluralItems(timeline.itemsWithoutPrice)} без цены не ${pluralForm(timeline.itemsWithoutPrice, 'попала', 'попали', 'попали')} в денежный ряд.`}
                        {boughtBeforeFirstRecord > 0
                            && ` ${pluralItems(boughtBeforeFirstRecord)}`
                                + ` ${pluralForm(boughtBeforeFirstRecord, 'куплена', 'куплены', 'куплены')}`
                                + ` до первой записи и ${pluralForm(boughtBeforeFirstRecord, 'вошла', 'вошли', 'вошли')}`
                                + ' в стартовый остаток.'}
                    </p>
                )}
            </section>
        </div>
    );
}
